import { IDropdownOption, MessageBarType, Toggle } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  BaseDialog,
  BasicList,
  basicListStylesInput,
  Button,
  buttonStylesDanger,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  hasChangedFieldValues,
  Item,
  itemStylesDropdown,
  Link,
  linkStylesGhost,
  MessageBar,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  ActivePermission,
  CoreService,
  FeatureSet,
  FeatureSetRequestId,
  FeatureSetResponse,
  ProcessIntervalUnit,
  UpdatableFeatureSetField,
  UpdateFeatureSetRequest,
  UpdateFeatureSetResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  FeatureSetFlow,
  FeatureSetType,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
import {
  OfflineTimeToLiveInterval,
  OnlineTimeToLiveInterval,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/TimeToLiveApi.pb';
import { buttonStylesPrimaryPanel, featureSetFlowNameMap } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { TextListEditor } from '../TextListEditor/TextListEditor';
import {
  editFeatureSetStylesDefault,
  IEditFeatureSetClassNames,
  IEditFeatureSetStyles,
  toggleStylesEditFeatureSet,
} from './EditFeatureSet.styles';

// online fields and customData are deprecated.
interface EditFeatureSetFields {
  tags?: string[];
  dataSourceDomains?: string[];
  description?: string;
  type?: FeatureSetType;
  deprecated?: boolean;
  processInterval?: number;
  processIntervalUnit?: ProcessIntervalUnit;
  timeToLiveOfflineInterval?: number;
  timeToLiveOfflineIntervalUnit?: OfflineTimeToLiveInterval;
  timeToLiveOnlineInterval?: number;
  timeToLiveOnlineIntervalUnit?: OnlineTimeToLiveInterval;
  legalApproved?: boolean;
  legalApprovedNotes?: string;
  state?: string;
  flow?: string;
  applicationName?: string;
  applicationId?: string;
  fieldsToUpdate?: UpdatableFeatureSetField[];
}

const getEditFeatureSetFields = (featureSet: FeatureSet = {}): EditFeatureSetFields => {
  const {
    tags,
    dataSourceDomains,
    description,
    featureSetType: type,
    deprecated,
    processInterval,
    processIntervalUnit,
    timeToLive: {
      ttlOffline: timeToLiveOfflineInterval,
      ttlOfflineInterval: timeToLiveOfflineIntervalUnit,
      ttlOnline: timeToLiveOnlineInterval,
      ttlOnlineInterval: timeToLiveOnlineIntervalUnit,
    } = {},
    specialData: { legal: { approved: legalApproved = false, notes: legalApprovedNotes = '' } = {} } = {},
    featureSetState: state,
    flow,
    applicationId,
    applicationName,
  } = featureSet;
  return {
    tags,
    dataSourceDomains,
    deprecated,
    description,
    type,
    processInterval,
    processIntervalUnit,
    timeToLiveOfflineInterval,
    timeToLiveOfflineIntervalUnit,
    timeToLiveOnlineInterval,
    timeToLiveOnlineIntervalUnit,
    legalApproved,
    legalApprovedNotes,
    state,
    flow,
    applicationName,
    applicationId,
  };
};

const getEmptyValues = (): EditFeatureSetFields => {
  return {
    tags: [],
    dataSourceDomains: [],
    deprecated: false,
    description: '',
    type: undefined,
    processInterval: 0,
    processIntervalUnit: undefined,
    timeToLiveOfflineInterval: 0,
    timeToLiveOfflineIntervalUnit: undefined,
    timeToLiveOnlineInterval: 0,
    timeToLiveOnlineIntervalUnit: undefined,
    legalApproved: false,
    legalApprovedNotes: '',
    state: '',
    flow: '',
    applicationName: '',
    applicationId: '',
  };
};

const featureSetTypeOptions: IDropdownOption[] = [
  { key: FeatureSetType.ENGINEERED, text: 'Engineered' },
  { key: FeatureSetType.RAW, text: 'Raw' },
];

const processIntervalUnitOptions: IDropdownOption[] = [
  { key: ProcessIntervalUnit.MONTHS, text: 'Months' },
  { key: ProcessIntervalUnit.DAYS, text: 'Days' },
  { key: ProcessIntervalUnit.HOURS, text: 'Hours' },
  { key: ProcessIntervalUnit.MINUTES, text: 'Minutes' },
  { key: ProcessIntervalUnit.SECONDS, text: 'Seconds' },
  { key: ProcessIntervalUnit.MILLISECONDS, text: 'Milliseconds' },
];

const timeToLiveOfflineIntervalUnitOptions: IDropdownOption[] = [
  { key: OfflineTimeToLiveInterval.YEARS, text: 'Years' },
  { key: OfflineTimeToLiveInterval.MONTHS, text: 'Months' },
  { key: OfflineTimeToLiveInterval.DAYS, text: 'Days' },
  { key: OfflineTimeToLiveInterval.HOURS, text: 'Hours' },
];

const timeToLiveOnlineIntervalUnitOptions: IDropdownOption[] = [
  { key: OnlineTimeToLiveInterval.MONTHS, text: 'Months' },
  { key: OnlineTimeToLiveInterval.DAYS, text: 'Days' },
  { key: OnlineTimeToLiveInterval.HOURS, text: 'Hours' },
  { key: OnlineTimeToLiveInterval.MINUTES, text: 'Minutes' },
];

const flowOptions: IDropdownOption[] = [
  {
    key: FeatureSetFlow.OFFLINE_ONLINE_AUTOMATIC,
    text: featureSetFlowNameMap.get(FeatureSetFlow.OFFLINE_ONLINE_AUTOMATIC) || '',
  },
  {
    key: FeatureSetFlow.OFFLINE_ONLINE_MANUAL,
    text: featureSetFlowNameMap.get(FeatureSetFlow.OFFLINE_ONLINE_MANUAL) || '',
  },
  { key: FeatureSetFlow.OFFLINE_ONLY, text: featureSetFlowNameMap.get(FeatureSetFlow.OFFLINE_ONLY) || '' },
  { key: FeatureSetFlow.ONLINE_ONLY, text: featureSetFlowNameMap.get(FeatureSetFlow.ONLINE_ONLY) || '' },
];

const getRequest = (
  previousValues: EditFeatureSetFields,
  values: EditFeatureSetFields,
  featureSet: FeatureSet
): UpdateFeatureSetRequest => {
  const { id: featureSetId, version: featureSetVersion } = featureSet;
  const fieldMap = new Map<string, any>([
    ['tags', UpdatableFeatureSetField.FEATURE_SET_TAGS],
    ['dataSourceDomains', UpdatableFeatureSetField.FEATURE_SET_DATA_SOURCE_DOMAINS],
    ['description', UpdatableFeatureSetField.FEATURE_SET_DESCRIPTION],
    ['type', UpdatableFeatureSetField.FEATURE_SET_TYPE],
    ['deprecated', UpdatableFeatureSetField.FEATURE_SET_DEPRECATED],
    ['processInterval', UpdatableFeatureSetField.FEATURE_SET_PROCESS_INTERVAL],
    ['processIntervalUnit', UpdatableFeatureSetField.FEATURE_SET_PROCESS_INTERVAL_UNIT],
    ['timeToLiveOfflineInterval', UpdatableFeatureSetField.FEATURE_SET_TTL_OFFLINE],
    ['timeToLiveOfflineIntervalUnit', UpdatableFeatureSetField.FEATURE_SET_TTL_OFFLINE_INTERVAL],
    ['timeToLiveOnlineInterval', UpdatableFeatureSetField.FEATURE_SET_TTL_ONLINE],
    ['timeToLiveOnlineIntervalUnit', UpdatableFeatureSetField.FEATURE_SET_TTL_ONLINE_INTERVAL],
    ['legalApproved', UpdatableFeatureSetField.FEATURE_SET_SPECIAL_DATA_LEGAL_APPROVED],
    ['legalApprovedNotes', UpdatableFeatureSetField.FEATURE_SET_SPECIAL_DATA_LEGAL_NOTES],
    ['state', UpdatableFeatureSetField.FEATURE_SET_STATE],
    ['flow', UpdatableFeatureSetField.FEATURE_SET_FLOW],
    ['applicationName', UpdatableFeatureSetField.FEATURE_SET_APPLICATION_NAME],
    ['applicationId', UpdatableFeatureSetField.FEATURE_SET_APPLICATION_ID],
  ]);
  const req: UpdateFeatureSetRequest = {
    featureSetId,
    featureSetVersion,
    fieldsToUpdate: [],
  };
  Object.keys(values).forEach((field) => {
    const newValue = values[field as keyof EditFeatureSetFields];
    const value = previousValues[field as keyof EditFeatureSetFields];
    if (value !== newValue) {
      req.fieldsToUpdate?.push(fieldMap.get(field));
      req[field as keyof UpdateFeatureSetRequest] = newValue;
    }
  });
  return req;
};

const isOwner = (permission?: ActivePermission) => permission === ActivePermission.ACTIVE_PERMISSION_OWNER;

interface IEditFeatureSetProps {
  id: string;
  permission?: ActivePermission;
  onUpdate?: (deleted?: boolean, updatedFeatureSet?: FeatureSet) => void;
  onCancel?: () => void;
}

export const EditFeatureSet = ({ id, permission, onUpdate, onCancel }: IEditFeatureSetProps) => {
  const refFeatureSet = useRef<FeatureSet>({});
  const refPreviousValues = useRef<EditFeatureSetFields>({});
  const refTempValues = useRef<{
    tag: string;
    dataSourceDomain: string;
  }>({ tag: '', dataSourceDomain: '' });
  const [values, setValues] = useState<EditFeatureSetFields>(getEmptyValues());
  const { addToast } = useToast();
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [isReadyToDelete, setIsReadyToDelete] = useState(false);
  const load = useCallback(async () => {
    try {
      const req: FeatureSetRequestId = { featureSetId: id };
      const { featureSet = {} }: FeatureSetResponse = await fsApi(CoreService.GetFeatureSetById, req);
      const previousValues = getEditFeatureSetFields(featureSet);
      refFeatureSet.current = featureSet;
      refPreviousValues.current = previousValues;
      setValues({ ...previousValues });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Feature Set: `));
    }
  }, [addToast]);
  const updateFeatureSet = useCallback(async (updatedValues: EditFeatureSetFields) => {
    let featureSet: FeatureSet | undefined;
    try {
      const { updatedFeatureSet }: UpdateFeatureSetResponse = await fsApi(
        CoreService.UpdateFeatureSet,
        getRequest(refPreviousValues.current, updatedValues, refFeatureSet.current)
      );
      featureSet = updatedFeatureSet;
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Feature set has been updated successfully!',
      });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while updating the feature set: `));
    } finally {
      onUpdate && onUpdate(false, featureSet);
    }
  }, []);
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      updateFeatureSet(values);
      e.preventDefault();
    },
    [values]
  );
  const onDelete = useCallback(async () => {
    try {
      const req: FeatureSetRequestId = { featureSetId: id };
      await fsApi(CoreService.DeleteFeatureSet, req);
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Feature set has been deleted successfully!',
      });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while deleting your feature set: `));
    } finally {
      if (onUpdate) onUpdate(true);
    }
  }, []);
  useEffect(() => {
    load();
  }, []);
  const classNames = useClassNames<IEditFeatureSetStyles, IEditFeatureSetClassNames>(
    'EditFeatureSet',
    editFeatureSetStylesDefault
  );
  return (
    <div className={classNames.root} data-test="edit-feature-set">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.fields}>
          <TextField
            name="description"
            label="Description"
            multiline
            rows={3}
            value={values.description}
            onChange={(_e, description) => {
              setValues({ ...values, description });
            }}
          />
          <Dropdown
            styles={{ root: { width: '120px' } }}
            label="Feature Set Type"
            placeholder="Select Feature Set Type"
            selectedKey={values.type}
            options={featureSetTypeOptions}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, type: option.key as FeatureSetType });
              }
            }}
          />
          <div className={classNames.subTitle}>Time to Live</div>
          <TextField
            name="timeToLiveOfflineInterval"
            label="TTL Offline Interval"
            type="number"
            value={String(values.timeToLiveOfflineInterval)}
            onChange={(_e, timeToLiveOfflineInterval) => {
              if (timeToLiveOfflineInterval !== undefined) {
                setValues({ ...values, timeToLiveOfflineInterval: +timeToLiveOfflineInterval });
              }
            }}
          />
          <Dropdown
            label="TTL Offline Interval Unit"
            placeholder="Select Interval Unit"
            selectedKey={values.timeToLiveOfflineIntervalUnit}
            options={timeToLiveOfflineIntervalUnitOptions}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, timeToLiveOfflineIntervalUnit: option.key as OfflineTimeToLiveInterval });
              }
            }}
          />
          <TextField
            name="timeToLiveOnlineInterval"
            label="TTL Online Interval"
            type="number"
            value={String(values.timeToLiveOnlineInterval)}
            onChange={(_e, timeToLiveOnlineInterval) => {
              if (timeToLiveOnlineInterval !== undefined) {
                setValues({ ...values, timeToLiveOnlineInterval: +timeToLiveOnlineInterval });
              }
            }}
          />
          <Dropdown
            label="TTL Online Interval Unit"
            placeholder="Select Interval Unit"
            selectedKey={values.timeToLiveOnlineIntervalUnit}
            options={timeToLiveOnlineIntervalUnitOptions}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, timeToLiveOnlineIntervalUnit: option.key as OnlineTimeToLiveInterval });
              }
            }}
          />
          <TextField
            name="state"
            label="State"
            value={values.state}
            onChange={(_e, state) => {
              setValues({ ...values, state });
            }}
          />
          <Dropdown
            label="Flow"
            placeholder="Select Feature Set Flow"
            selectedKey={values.flow}
            options={flowOptions}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, flow: option.key as FeatureSetFlow });
              }
            }}
          />
          <Toggle
            styles={toggleStylesEditFeatureSet}
            id="deprecated"
            label="Deprecated"
            inlineLabel
            checked={values.deprecated}
            onChange={(_e, deprecated) => {
              setValues({ ...values, deprecated });
            }}
          />
          <div className={classNames.subTitle}>Tags</div>
          <TextListEditor
            key="tag-list"
            placeholder="Input Tag with Enter"
            items={values.tags}
            onChange={(tags) => setValues({ ...values, tags })}
          />
          <div className={classNames.subTitle}>Data Source Domains</div>
          <div>
            <div className={classNames.fieldItem}>
              <TextField
                name="dataSourceDomain"
                onChange={(_e, dataSourceDomain = '') => (refTempValues.current.dataSourceDomain = dataSourceDomain)}
              />
              <Button
                styles={buttonStylesGhost}
                text="Add"
                onClick={() => {
                  const dataSourceDomain = refTempValues.current.dataSourceDomain;
                  if (dataSourceDomain) {
                    const dataSourceDomains = values.dataSourceDomains || [];
                    if (!dataSourceDomains.includes(dataSourceDomain)) {
                      dataSourceDomains.push(dataSourceDomain);
                      setValues({ ...values, dataSourceDomains });
                    }
                  }
                }}
              />
            </div>
            <BasicList
              styles={basicListStylesInput}
              idField="key"
              labelField="text"
              data={values.dataSourceDomains?.map((d) => ({ key: d, text: d }))}
              horizontal
              itemRenderer={(d: IDropdownOption) => (
                <Item
                  styles={itemStylesDropdown}
                  hasClose
                  data={d}
                  idField="key"
                  labelField="text"
                  onClose={(d) => {
                    setValues({
                      ...values,
                      dataSourceDomains: values.dataSourceDomains?.filter((item) => item !== d.key),
                    });
                  }}
                />
              )}
            />
          </div>
          <div className={classNames.subTitle}>Process Interval</div>
          <TextField
            name="processInterval"
            label="Process Interval"
            type="number"
            value={String(values.processInterval)}
            onChange={(_e, processInterval) => {
              if (processInterval !== undefined) {
                setValues({ ...values, processInterval: +processInterval });
              }
            }}
          />
          <Dropdown
            label="Process Interval Unit"
            placeholder="Select Interval Unit"
            selectedKey={values.processIntervalUnit}
            options={processIntervalUnitOptions}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, processIntervalUnit: option.key as ProcessIntervalUnit });
              }
            }}
          />
          <div className={classNames.subTitle}>Legal Approved</div>
          <Toggle
            styles={toggleStylesEditFeatureSet}
            id="legalApproved"
            label="Legal Approved"
            inlineLabel
            checked={values.legalApproved}
            onChange={(_e, legalApproved) => {
              setValues({ ...values, legalApproved });
            }}
          />
          <TextField
            name="legalApprovedNotes"
            label="Legal Approved Notes"
            multiline
            rows={3}
            value={values.legalApprovedNotes}
            onChange={(_e, legalApprovedNotes) => {
              setValues({ ...values, legalApprovedNotes });
            }}
          />
          <div className={classNames.subTitle}>Application</div>
          <TextField
            name="applicationId"
            label="Application Id"
            value={values.applicationId}
            onChange={(_e, applicationId) => {
              setValues({ ...values, applicationId });
            }}
          />
          <TextField
            name="applicationName"
            label="Application Name"
            value={values.applicationName}
            onChange={(_e, applicationName) => {
              setValues({ ...values, applicationName });
            }}
          />
        </div>
        <div className={classNames.buttons}>
          <div>
            {isOwner(permission) && (
              <Button
                tabIndex={-1}
                styles={buttonStylesDanger}
                text="Delete"
                onClick={toggleHideDialog}
                data-test="button-delete"
              />
            )}
          </div>
          <div>
            <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
            <Button
              styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
              type="submit"
              text="Save"
              disabled={!hasChangedFieldValues(refPreviousValues.current, values)}
            />
          </div>
        </div>
      </form>
      <BaseDialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        title={`Delete ${refFeatureSet.current.featureSetName}`}
        content={
          <>
            <MessageBar messageBarType={MessageBarType.error}>
              Delete a feature set
              <br />
              Are you sure you want to delete <strong>{refFeatureSet.current.featureSetName}</strong>?
            </MessageBar>
            <div style={{ marginTop: 16 }}>
              <TextField
                label="Please type the feature set name to be deleted"
                placeholder="Feature Set name"
                onChange={(_e, value) => setIsReadyToDelete(value === refFeatureSet.current.featureSetName)}
              />
            </div>
          </>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }} data-test="dialog-footer">
            <Button
              disabled={!isReadyToDelete}
              styles={buttonStylesDanger}
              text="Delete"
              onClick={() => {
                onDelete();
                toggleHideDialog();
              }}
              data-test="dialog-button-delete"
            />
            <Link styles={[linkStylesGhost, { root: { marginRight: 8 } }]} onClick={toggleHideDialog}>
              Cancel
            </Link>
          </div>
        }
      ></BaseDialog>
    </div>
  );
};
